import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { TraceKey } from '@ulysses-inc/harami_api_client'
import { FC, useCallback, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useMutationDeleteTraceKey } from 'src/features/traceKeys/api/useMutationDeleteTraceKey'
import { useQueryTraceKeyTemplates } from 'src/features/traceKeys/api/useQueryTraceKeyTemplates'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import { LinkedTemplatesList } from './components/LinkedTemplatesList'

type Props = {
  open: boolean
  traceKey: TraceKey
  onClose: () => void
}

/**
 * 検索キー削除確認ダイアログ
 *
 * - 削除のリスクを把握できる
 * - 誤削除防止のため検索キー名の入力確認を行う
 */
export const DeleteConfirmDialog: FC<Props> = ({ open, traceKey, onClose }) => {
  const [confirmInput, setConfirmInput] = useState('')
  const { data: templatesData } = useQueryTraceKeyTemplates(traceKey.id)
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const deleteTraceKeyMutation = useMutationDeleteTraceKey()

  const templates = templatesData?.templates ?? []
  const hasLinkedTemplates = templates.length > 0
  const isConfirmInputValid = confirmInput === traceKey.name

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setConfirmInput(event.target.value)
    },
    [],
  )

  const handleClose = useCallback(() => {
    setConfirmInput('')
    onClose()
  }, [onClose])

  const handleDialogClose = useModalClosePreventionIsPending(
    handleClose,
    deleteTraceKeyMutation.isPending,
  )

  const handleDelete = useCallback(() => {
    deleteTraceKeyMutation.mutate(traceKey.id, {
      onSuccess: () => {
        showSuccessSnackbar(`「${traceKey.name}」を削除しました`)
        handleClose()
      },
      onError: () => {
        showErrorSnackbar(`「${traceKey.name}」の削除に失敗しました`)
      },
    })
  }, [
    deleteTraceKeyMutation,
    traceKey.id,
    traceKey.name,
    showSuccessSnackbar,
    showErrorSnackbar,
    handleClose,
  ])

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      onClick={e => e.stopPropagation()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-confirm-dialog-title"
    >
      <DialogTitle id="delete-confirm-dialog-title">
        「{traceKey.name}」の削除を実行しますがよろしいですか？
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography component="div">
            <Typography color="error" fontWeight="bold">
              この操作は元に戻すことはできません。
            </Typography>
            {hasLinkedTemplates
              ? `「${traceKey.name}」は ${templates.length}個のひな形と紐付けています。`
              : `「${traceKey.name}」に紐づいているひな形はありません。`}
            <br />
            削除すると
            <Typography component="span" fontWeight="bold">
              紐づく全てのレポート
            </Typography>
            は「{traceKey.name}」で検索できなくなります。
          </Typography>

          {hasLinkedTemplates && <LinkedTemplatesList templates={templates} />}

          <Stack spacing={1}>
            <Typography>
              消し間違いを防ぐために、「
              <Typography component="span" fontWeight="bold">
                {traceKey.name}
              </Typography>
              」と入力してください。
            </Typography>
            <TextField
              value={confirmInput}
              onChange={handleInputChange}
              placeholder={traceKey.name}
              size="small"
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={deleteTraceKeyMutation.isPending}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!isConfirmInputValid || deleteTraceKeyMutation.isPending}
        >
          削除
        </Button>
      </DialogActions>
    </Dialog>
  )
}
